// TODO - Type 'any' needs to be fixed.
/* eslint-disable simple-import-sort/imports */
import {
	Box,
	Button,
	CircularProgress,
	Tooltip,
	Typography,
} from "@mui/material";
import React from "react";
import { truncateFileName } from "../../helper";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useMemo } from "react";
import { format, isFuture } from "date-fns";
import useWorkersData from "../../../../../../ts/components/workers/controllers/use-workers-data";
import {
	deleteWorkerCertifications,
	downloadWorkerCertification,
	openEditWorkerCertificationDialog,
} from "../../../../../../export/workers";
import Highlight from "../../../../Highlight";
import SkillColumn from "../ui/table/skill-column";
import { hasPermission } from "../../../../../utils/helpers";

interface GridColumn {
	field: string;
	headerName?: string;
	// minWidth: number | string;
	renderHeader?: (params: any) => JSX.Element;
	renderCell?: (params: any) => JSX.Element;
	sortable?: boolean;
	disableColumnMenu?: boolean;
	cellClassName?: string;
	headerClassName?: string;
	minWidth?: number;
	flex?: number;
	valueGetter?: (params: any) => any;
	filterable?: boolean;
}

const useWorkerCertificationColumn = (searchText: string) => {
	const confirm = useConfirm();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { allSkills, downloadProgress, userRolesWithPermission} = useWorkersData();

	const deleteContent = ({ certName }: { certName: string }) => (
		<Box>
			<Typography
				sx={{
					fontFamily: "Roboto",
					fontSize: "16px",
					fontWeight: 400,
					lineHeight: "28px",
				}}
			>
				{t("workers_certification-will-be-removed", { certName })}
			</Typography>
		</Box>
	);

	const workerProfile = useSelector(
		(state: any) => state.manageWorkers.workerProfile.profile
	);

	const { userWorkCenters } = useSelector((state: any) => ({
		userWorkCenters:
			state.manageWorkers.filterData.allWorkcenters?.map((wc: any) => String(wc.id)) ||
			[],
	}));

	const isInUserWorkcenter = useMemo(() => {
		const workerWorkCenterId = workerProfile?.workCenter?.id;
		if (!workerWorkCenterId) return true;
		return userWorkCenters.includes(String(workerWorkCenterId));
	}, [workerProfile?.workCenter?.id, userWorkCenters]);

	const handleIsInUserWorkcenterAlert = useCallback(() => {
		confirm({
			title: "",
			description: t("workers_not-in-workcenter-alert"),
			confirmationText: t("common_close"),
			cancellationText: "",
		});
	}, [confirm, t]);

	const handleDelete = (workerCertId: string, certName: string) => {
		if (!workerCertId) return;
		confirm({
			title: t("workers_revoke-certification-for-worker"),
			content: deleteContent({ certName: certName }),
			cancellationText: t("common_cancel"),
			confirmationText: (
				<Button variant='contained' color='error'>
					{t("workers_revoke")}
				</Button>
			),
		})
			.then(() =>
				dispatch(
					deleteWorkerCertifications({
						workerCertId: workerCertId,
					})
				)
			)
			.catch(() => console.log("Deletion cancelled."));
	};

	const columns: GridColumn[] = [
		{
			field: "certificationName",
			headerName: t("workers_certification-name"),
			minWidth: 185,
			flex: 1,
			renderHeader: () => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_certification-name")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				return (
					<Box mt={1} mb={1}>
						<Highlight value={params.row.certName} searchText={searchText} />
					</Box>
				);
			},
			valueGetter: (params: any) => {
				return params.row.certName;
			},
		},
		{
			field: "status",
			minWidth: 130,
			flex: 1,
			filterable: false,
			disableColumnMenu: true,
			renderHeader: (params: any) => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_status")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				// console.log(params.row);
				const validFrom = params.row.validFrom;
				const validUntil = params.row.validTo;

				if (!validFrom || isFuture(new Date(validFrom))) return <></>;
				if (!validUntil || isFuture(new Date(validUntil)))
					return (
						<Box>
							<Highlight searchText={searchText} value={t("workers_active")} />
						</Box>
					);

				return (
					<Box>
						<Highlight
							searchText={searchText}
							value={t("workers_expired")}
							color='#E44B48'
						/>
					</Box>
				);
			},
			valueGetter: (params: any) => {
				const validFrom = params.row.validFrom;
				const validUntil = params.row.validTo;

				if (!validFrom || isFuture(new Date(validFrom))) return "";
				if (!validUntil || isFuture(new Date(validUntil)))
					return t("workers_active");
				return t("workers_expired");
			},
		},
		{
			field: "associatedSkills",
			minWidth: 300,
			flex: 1,
			filterable: false,
			disableColumnMenu: true,
			renderHeader: (params: any) => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_workers-skills")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				if (!params.row.skillIds) return <>-</>;
				const skillIds = params.row.skillIds
					?.split(",")
					?.map((id: string) => parseInt(id));
				const skills = allSkills.filter((skill: { id: number }) =>
					skillIds.includes(skill.id)
				);
				// const { skillString, remainig, seeMore } = generateSkillString(skills);
				// const skillsText = skillString + remainig;

				if (skills.length === 0) return <Box>-</Box>;
				return (
					<Box width={260} display='flex'>
						<SkillColumn skills={skills} searchText={searchText} />
					</Box>
				);
			},
			valueGetter: (params: any) => {
				return params.row.skillIds?.split(",")?.length;
			},
		},
		{
			field: "validFrom",
			minWidth: 130,
			flex: 1,
			filterable: false,
			disableColumnMenu: true,
			renderHeader: (params: any) => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_valid-from")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				if (!params.row.validFrom) return <>-</>;
				const datetime = params.row.validFrom + "T00:00:00";
				const validFromDate = format(new Date(datetime), "MM/dd/yyyy");

				return (
					<Box>
						<Highlight searchText={searchText} value={validFromDate} />
					</Box>
				);
			},
			valueGetter: (params: any) => {
				return params.row.validFrom;
			},
		},
		{
			field: "validUntil",
			minWidth: 130,
			flex: 1,
			filterable: false,
			disableColumnMenu: true,
			renderHeader: (params: any) => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_valid-until")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				if (!params.row.validTo) return <>-</>;

				const datetime = params.row.validTo + "T00:00:00";
				const validToDate = format(new Date(datetime), "MM/dd/yyyy");

				return (
					<Box>
						<Highlight searchText={searchText} value={validToDate} />
					</Box>
				);
			},
			valueGetter: (params: any) => {
				return params.row.validTo;
			},
		},
		{
			field: "attachments",
			minWidth: 195,
			flex: 1,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderHeader: (params: any) => {
				return (
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "14px",
								color: "#000000",
								letterSpacing: "0.17px",
							}}
						>
							{t("workers_attachments")}
						</Typography>
					</Box>
				);
			},
			renderCell: (params: any) => {
				const workerCertFiles = params.row.workerCertFiles;
				return (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
						mt={1}
						mb={1}
					>
						{workerCertFiles
							? workerCertFiles?.map((file: any) => {
									return (
                    <Box
                      key={file.workerCertFileId}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Tooltip
                        title={file.fileName}
                        placement="bottom-start"
                        disableInteractive
                      >
                        <Typography
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "18px",
                            color: "#2F4D8B",
                            letterSpacing: "0.17px",
                            cursor: "pointer",
                            textDecoration: "underline",
                            // textOverflow: "ellipsis",
                            // maxWidth: "180px",
                            // whiteSpace: "nowrap",
                            // overflow: "hidden",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            dispatch(
                              downloadWorkerCertification({
                                workerCertFileId: file.workerCertFileId,
                                fileName: file.fileName,
                              })
                            );
                          }}
                        >
                          {truncateFileName(file.fileName, 15)}
                        </Typography>
                      </Tooltip>
                      {downloadProgress[file.workerCertFileId] !==
                        undefined && (
                        <CircularProgress
                          value={downloadProgress[file.workerCertFileId]}
                          size={10}
                          variant="determinate"
                        />
                      )}
                    </Box>
                  );
									// eslint-disable-next-line no-mixed-spaces-and-tabs
							  })
							: "-"}
					</Box>
				);
			},
		}
	];

  if (hasPermission(userRolesWithPermission, "SETTINGS.EDIT_WORKERS")) {
    columns.push({
      field: "actions",
      minWidth: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      cellClassName: "hover-column-cell",
      headerClassName: "hover-column-header",
      renderHeader: (params: any) => <></>,
      renderCell: (params: any) => {
        return (
          <Box
            className="hover-content"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "80px",
              padding: "12px 16px 8px 16px",
              backgroundColor: "#FFF",
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                color: "#6c6c6c",
                "&:hover": {
                  color: "#000000",
                  transform: "scale(1.2)",
                },
                cursor: "pointer",
              }}
            >
              <Tooltip title={t("common_edit")} disableInteractive>
                <EditIcon
                  id={`edit-job-${params?.row?.id}-enabled`}
                  fontSize="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isInUserWorkcenter) {
                      dispatch(openEditWorkerCertificationDialog(params.row));
                    } else {
                      handleIsInUserWorkcenterAlert();
                    }
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                color: "#6c6c6c",
                "&:hover": {
                  color: "red",
                  transform: "scale(1.2)",
                },
                cursor: "pointer",
              }}
            >
              <Tooltip title={t("workers_delete")} disableInteractive>
                <DeleteIcon
                  id={`delete-job-${params?.row?.id}-enabled`}
                  sx={{
                    marginLeft: "8px",
                  }}
                  fontSize="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isInUserWorkcenter) {
                      handleDelete(params.row.workerCertId, params.row.certName);
                    } else {
                      handleIsInUserWorkcenterAlert();
                    }
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
        );
      },
    })
  }

	return columns;
};

export default useWorkerCertificationColumn;
